import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Download, RefreshCw, Filter, Search, ArrowDownCircle, ArrowUpCircle, Calendar, Building2, AlertCircle, Database, Cloud, Clock, Eye, X, Printer, FileSpreadsheet } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Excel export fonksiyonu - Türkçe karakter destekli
const exportToExcel = (data, columns, fileName) => {
  // BOM for UTF-8
  const BOM = '\uFEFF';
  
  // Header row
  const headerRow = columns.map(col => col.header).join('\t');
  
  // Data rows
  const dataRows = data.map(row => {
    return columns.map(col => {
      let value = col.accessor(row);
      if (value === null || value === undefined) value = '';
      // Escape special characters
      value = String(value).replace(/\t/g, ' ').replace(/\n/g, ' ');
      return value;
    }).join('\t');
  }).join('\n');
  
  const content = BOM + headerRow + '\n' + dataRows;
  const blob = new Blob([content], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${fileName}.xls`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// PDF export fonksiyonu - Türkçe karakter destekli
const exportToPDF = (data, columns, title, fileName) => {
  const printWindow = window.open('', '_blank');
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${title}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        h1 { color: #333; font-size: 18px; margin-bottom: 20px; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        th { background-color: #6366f1; color: white; padding: 8px; text-align: left; }
        td { padding: 6px 8px; border-bottom: 1px solid #ddd; }
        tr:nth-child(even) { background-color: #f9fafb; }
        .total-row { font-weight: bold; background-color: #e5e7eb; }
        @media print { body { -webkit-print-color-adjust: exact; } }
      </style>
    </head>
    <body>
      <h1>${title}</h1>
      <table>
        <thead>
          <tr>${columns.map(col => `<th>${col.header}</th>`).join('')}</tr>
        </thead>
        <tbody>
          ${data.map(row => `
            <tr>
              ${columns.map(col => `<td>${col.accessor(row) ?? ''}</td>`).join('')}
            </tr>
          `).join('')}
        </tbody>
      </table>
      <script>window.onload = function() { window.print(); }</script>
    </body>
    </html>
  `;
  printWindow.document.write(htmlContent);
  printWindow.document.close();
};

// Şirketler
const SIRKETLER = [
  { id: 'miba', ad: 'MİBA', color: 'bg-blue-500', textColor: 'text-blue-500' },
  { id: 'odak_karabaglar', ad: 'ODAK KARABAĞLAR', color: 'bg-green-500', textColor: 'text-green-500' },
  { id: 'odak_6site', ad: 'ODAK 6.SİTE', color: 'bg-orange-500', textColor: 'text-orange-500' }
];

// Fatura kaynakları ve türleri - Genişletilmiş
const FATURA_KAYNAKLARI = [
  // EDM
  { id: 'edm_satis', kaynak: 'edm', tur: 'giden', faturaTipi: 'satis', label: 'EDM Satış', icon: Cloud, color: 'bg-blue-500', desc: 'EDM satış faturaları' },
  { id: 'edm_alis', kaynak: 'edm', tur: 'gelen', faturaTipi: 'alis', label: 'EDM Alış', icon: Cloud, color: 'bg-emerald-500', desc: 'EDM alış faturaları' },
  { id: 'edm_iade_giden', kaynak: 'edm', tur: 'giden', faturaTipi: 'iade_giden', label: 'EDM İade Giden', icon: Cloud, color: 'bg-orange-500', desc: 'EDM satış iade faturaları' },
  { id: 'edm_iade_gelen', kaynak: 'edm', tur: 'gelen', faturaTipi: 'iade_gelen', label: 'EDM İade Gelen', icon: Cloud, color: 'bg-red-500', desc: 'EDM alış iade faturaları' },
  // Netsis
  { id: 'netsis_satis', kaynak: 'netsis', tur: 'giden', faturaTipi: 'satis', label: 'Netsis Satış', icon: Database, color: 'bg-indigo-500', desc: 'Netsis satış faturaları' },
  { id: 'netsis_alis', kaynak: 'netsis', tur: 'gelen', faturaTipi: 'alis', label: 'Netsis Alış', icon: Database, color: 'bg-purple-500', desc: 'Netsis alış faturaları' },
  { id: 'netsis_iade_giden', kaynak: 'netsis', tur: 'giden', faturaTipi: 'iade_giden', label: 'Netsis İade Giden', icon: Database, color: 'bg-amber-500', desc: 'Netsis satış iade faturaları' },
  { id: 'netsis_iade_gelen', kaynak: 'netsis', tur: 'gelen', faturaTipi: 'iade_gelen', label: 'Netsis İade Gelen', icon: Database, color: 'bg-pink-500', desc: 'Netsis alış iade faturaları' }
];

// Fatura tipi etiketleri
const FATURA_TIPI_LABELS = {
  'satis': 'Satış',
  'alis': 'Alış',
  'iade_giden': 'İade Giden',
  'iade_gelen': 'İade Gelen'
};

// Auto-refresh interval (30 seconds)
const AUTO_REFRESH_INTERVAL = 30000;

export default function Faturalar() {
  const [selectedSirket, setSelectedSirket] = useState('miba');
  const [selectedKaynak, setSelectedKaynak] = useState('edm_satis');
  const [faturalar, setFaturalar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Bugünün tarihini al - component mount'ta bir kez hesapla
  const [dateFilter, setDateFilter] = useState(() => {
    const today = new Date().toISOString().split('T')[0];
    return { baslangic: today, bitis: today };
  });
  
  const [stats, setStats] = useState({ toplam: 0, toplamTutar: 0, hafizadakiToplam: 0 });
  const [apiStatus, setApiStatus] = useState({ 
    miba: { edm: false, netsis: false }, 
    odak_karabaglar: { edm: false, netsis: false }, 
    odak_6site: { edm: false, netsis: false } 
  });
  const [error, setError] = useState(null);
  const [sonSync, setSonSync] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [hafizaOzet, setHafizaOzet] = useState([]);
  const [gunlukOzet, setGunlukOzet] = useState({});
  
  // Fatura görüntüleme modal state
  const [viewingFatura, setViewingFatura] = useState(null);
  const [faturaHtml, setFaturaHtml] = useState(null);
  const [faturaPdfUrl, setFaturaPdfUrl] = useState(null);
  const [loadingFatura, setLoadingFatura] = useState(false);
  const [viewMode, setViewMode] = useState('pdf'); // 'pdf' veya 'html'
  
  // Karşılaştırma modal state
  const [showKarsilastirma, setShowKarsilastirma] = useState(false);
  const [karsilastirmaLoading, setKarsilastirmaLoading] = useState(false);
  const [karsilastirmaSonuc, setKarsilastirmaSonuc] = useState(null);
  const [karsilastirmaTarih, setKarsilastirmaTarih] = useState({ 
    baslangic: '2026-01-01', 
    bitis: '2026-01-15' 
  });
  const [karsilastirmaTipi, setKarsilastirmaTipi] = useState('satis');
  const [karsilastirmaArama, setKarsilastirmaArama] = useState('');

  const currentKaynak = FATURA_KAYNAKLARI.find(k => k.id === selectedKaynak);

  // Fatura görüntüle - PDF URL'ini doğrudan API'den al
  const viewFatura = async (fatura, forceRefresh = false) => {
    setViewingFatura(fatura);
    setLoadingFatura(true);
    setFaturaHtml(null);
    setFaturaPdfUrl(null);
    setViewMode('pdf');
    
    // EDM faturası için doğrudan PDF URL'i kullan
    if (fatura.kaynak === 'edm') {
      setFaturaPdfUrl(`${API_URL}/api/faturalar/${fatura.id}/pdf`);
      setViewMode('pdf');
      setLoadingFatura(false);
      return;
    }
    
    // Diğer kaynaklar için HTML'e düş
    try {
      const url = forceRefresh 
        ? `${API_URL}/api/faturalar/${fatura.id}/html?force_refresh=true`
        : `${API_URL}/api/faturalar/${fatura.id}/html`;
      
      const response = await fetch(url);
      if (response.ok) {
        const text = await response.text();
        try {
          const data = JSON.parse(text);
          setFaturaHtml(data.html);
        } catch (e) {
          setFaturaHtml(text);
        }
        setViewMode('html');
      }
    } catch (err) {
      console.error('Fatura yüklenemedi:', err);
    } finally {
      setLoadingFatura(false);
    }
  };

  // Faturayı yeniden çek (cache'i bypass et)
  const refreshFatura = () => {
    if (viewingFatura) {
      viewFatura(viewingFatura, true);
    }
  };

  // PDF indir
  const downloadPdf = async (fatura) => {
    try {
      const response = await fetch(`${API_URL}/api/faturalar/${fatura.id}/pdf`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fatura.faturaNo || 'fatura'}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('PDF indirilemedi. Fatura EDM\'de bulunamadı olabilir.');
      }
    } catch (err) {
      console.error('PDF indirme hatası:', err);
      alert('PDF indirme hatası: ' + err.message);
    }
  };

  // Modal kapat
  const closeFaturaModal = () => {
    setViewingFatura(null);
    setFaturaHtml(null);
    setFaturaPdfUrl(null);
  };

  // Faturayı yazdır
  const printFatura = () => {
    const printWindow = window.open('', '_blank');
    if (faturaHtml) {
      printWindow.document.write(faturaHtml);
    } else if (viewingFatura) {
      printWindow.document.write(generateFaturaHtml(viewingFatura));
    }
    printWindow.document.close();
    printWindow.print();
  };

  // EDM - Netsis Karşılaştırma
  const karsilastir = async () => {
    setKarsilastirmaLoading(true);
    setKarsilastirmaSonuc(null);
    try {
      const response = await fetch(`${API_URL}/api/faturalar/karsilastir`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          sirket: selectedSirket,
          baslangicTarihi: karsilastirmaTarih.baslangic,
          bitisTarihi: karsilastirmaTarih.bitis,
          faturaTipi: karsilastirmaTipi
        })
      });
      
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        throw new Error('Karşılaştırma verisi alınamadı');
      }
      
      if (data.success) {
        setKarsilastirmaSonuc(data);
      }
    } catch (err) {
      console.error('Karşılaştırma hatası:', err);
    } finally {
      setKarsilastirmaLoading(false);
    }
  };

  // Fatura HTML şablonu oluştur (EDM'den HTML gelmezse)
  const generateFaturaHtml = (fatura) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Fatura - ${fatura.faturaNo}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 40px; background: white; }
          .container { max-width: 210mm; margin: 0 auto; border: 1px solid #ddd; padding: 30px; }
          .header { display: flex; justify-content: space-between; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 20px; }
          .company { font-size: 24px; font-weight: bold; color: #333; }
          .invoice-info { text-align: right; }
          .invoice-no { font-size: 18px; font-weight: bold; color: #d35400; }
          .date { color: #666; margin-top: 5px; }
          .parties { display: flex; justify-content: space-between; margin: 30px 0; }
          .party { width: 45%; }
          .party-title { font-weight: bold; color: #666; margin-bottom: 10px; border-bottom: 1px solid #eee; padding-bottom: 5px; }
          .party-name { font-size: 16px; font-weight: bold; margin-bottom: 5px; }
          .party-detail { color: #666; font-size: 14px; }
          .amounts { margin-top: 40px; border-top: 2px solid #333; padding-top: 20px; }
          .amount-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee; }
          .amount-label { color: #666; }
          .amount-value { font-weight: bold; }
          .total-row { font-size: 18px; background: #f5f5f5; padding: 15px; margin-top: 10px; }
          .total-row .amount-value { color: #d35400; font-size: 20px; }
          .footer { margin-top: 40px; text-align: center; color: #999; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div class="company">E-FATURA</div>
            <div class="invoice-info">
              <div class="invoice-no">${fatura.faturaNo}</div>
              <div class="date">${new Date(fatura.tarih).toLocaleDateString('tr-TR')}</div>
            </div>
          </div>
          
          <div class="parties">
            <div class="party">
              <div class="party-title">${fatura.tur === 'gelen' ? 'SATICI' : 'ALICI'}</div>
              <div class="party-name">${fatura.firmaAdi || '-'}</div>
              <div class="party-detail">VKN: ${fatura.vergiNo || '-'}</div>
            </div>
            <div class="party">
              <div class="party-title">${fatura.tur === 'gelen' ? 'ALICI' : 'SATICI'}</div>
              <div class="party-name">${SIRKETLER.find(s => s.id === fatura.sirket)?.ad || fatura.sirket}</div>
              <div class="party-detail">VKN: ${fatura.sirket === 'miba' ? '6210534942' : '-'}</div>
            </div>
          </div>
          
          <div class="amounts">
            <div class="amount-row">
              <span class="amount-label">Matrah</span>
              <span class="amount-value">${new Intl.NumberFormat('tr-TR', { style: 'currency', currency: fatura.paraBirimi || 'TRY' }).format(fatura.matrah || 0)}</span>
            </div>
            <div class="amount-row">
              <span class="amount-label">KDV (%20)</span>
              <span class="amount-value">${new Intl.NumberFormat('tr-TR', { style: 'currency', currency: fatura.paraBirimi || 'TRY' }).format(fatura.kdvTutar || 0)}</span>
            </div>
            <div class="amount-row total-row">
              <span class="amount-label">TOPLAM</span>
              <span class="amount-value">${new Intl.NumberFormat('tr-TR', { style: 'currency', currency: fatura.paraBirimi || 'TRY' }).format(fatura.toplamTutar || 0)}</span>
            </div>
          </div>
          
          <div class="footer">
            Bu belge elektronik olarak oluşturulmuştur.<br>
            Kaynak: ${fatura.kaynak?.toUpperCase() || 'EDM'} | UUID: ${fatura.kaynakUuid || '-'}
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Faturaları getir
  const fetchFaturalar = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        sirket: selectedSirket,
        kaynak: currentKaynak.kaynak,
        tur: currentKaynak.tur,
        faturaTipi: currentKaynak.faturaTipi,
        baslangicTarihi: dateFilter.baslangic,
        bitisTarihi: dateFilter.bitis || new Date().toISOString().split('T')[0]
      });
      
      const response = await fetch(`${API_URL}/api/faturalar?${params}`);
      const text = await response.text();
      
      if (!response.ok) throw new Error('Faturalar yüklenemedi');
      
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        throw new Error('Fatura verisi okunamadı');
      }
      
      setFaturalar(data.faturalar || []);
      setStats({
        toplam: data.toplam || 0,
        toplamTutar: data.toplamTutar || 0
      });
    } catch (err) {
      setError(err.message);
      setFaturalar([]);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [selectedSirket, currentKaynak, dateFilter]);

  // Son senkronizasyon zamanını getir
  const fetchSonSync = async () => {
    try {
      const response = await fetch(`${API_URL}/api/faturalar/son-sync`);
      if (response.ok) {
        const text = await response.text();
        try {
          const data = JSON.parse(text);
          setSonSync(data.sonSync);
        } catch (e) {
          // ignore parse error
        }
      }
    } catch (err) {
      console.error('Son sync alınamadı:', err);
    }
  };

  // Tüm faturaları senkronize et
  const syncAllFaturalar = async () => {
    setSyncingAll(true);
    setError(null);
    
    // AbortController ile timeout ekle
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 dakika timeout
    
    try {
      const response = await fetch(`${API_URL}/api/faturalar/sync-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // Response body'yi güvenli şekilde oku
      let text = '';
      try {
        text = await response.text();
      } catch (readError) {
        console.error('Response read error:', readError);
        throw new Error('Sunucu yanıtı okunamadı. Bağlantı sorunu olabilir.');
      }
      
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        if (!response.ok) {
          throw new Error(`Sunucu hatası (${response.status}): ${text.substring(0, 100) || 'Boş yanıt'}`);
        }
        throw new Error('Sunucudan geçersiz yanıt alındı');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || `Senkronizasyon başarısız (${response.status})`);
      }
      
      const toplamYeni = data.sonuclar?.reduce((acc, s) => acc + (s.yeniKayit || 0), 0) || 0;
      const toplamGuncellenen = data.sonuclar?.reduce((acc, s) => acc + (s.guncellenen || 0), 0) || 0;
      
      alert(`Tüm şirketler senkronize edildi!\n${toplamYeni} yeni fatura, ${toplamGuncellenen} güncelleme`);
      fetchFaturalar();
      fetchSonSync();
    } catch (err) {
      clearTimeout(timeoutId);
      console.error('Sync-all hatası:', err);
      if (err.name === 'AbortError') {
        setError('Bağlantı zaman aşımına uğradı. Sunucu erişilebilir değil olabilir.');
      } else {
        setError(err.message);
      }
    } finally {
      setSyncingAll(false);
    }
  };

  // Komple Sync - EDM + Netsis birlikte (ERP'den Getir özelliği)
  const syncKomple = async () => {
    setSyncing(true);
    setError(null);
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 dakika timeout
    
    try {
      const response = await fetch(`${API_URL}/api/faturalar/sync-komple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sirket: selectedSirket,
          tur: currentKaynak.tur,
          faturaTipi: currentKaynak.faturaTipi,
          baslangicTarihi: dateFilter.baslangic,
          bitisTarihi: dateFilter.bitis || new Date().toISOString().split('T')[0]
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        let errorMsg = `Sunucu hatası (${response.status})`;
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorMsg;
        } catch (e) {}
        throw new Error(errorMsg);
      }
      
      const data = await response.json();
      
      // Detaylı sonuç göster
      let msg = '🔄 Komple Senkronizasyon Tamamlandı!\n\n';
      msg += `📊 NETSIS (ERP):\n`;
      msg += `   • Yeni: ${data.netsis?.yeniKayit || 0}\n`;
      msg += `   • Güncellenen: ${data.netsis?.guncellenen || 0}\n`;
      if (data.netsis?.hata) msg += `   ⚠️ Hata: ${data.netsis.hata}\n`;
      msg += `\n📧 EDM (e-Fatura):\n`;
      msg += `   • Yeni: ${data.edm?.yeniKayit || 0}\n`;
      msg += `   • Güncellenen: ${data.edm?.guncellenen || 0}\n`;
      if (data.edm?.hata) msg += `   ⚠️ Hata: ${data.edm.hata}\n`;
      msg += `\n✅ TOPLAM: ${data.toplamYeni || 0} yeni, ${data.toplamGuncellenen || 0} güncellendi`;
      
      alert(msg);
      
      fetchFaturalar();
      fetchSonSync();
      fetchHafizaOzet();
    } catch (err) {
      clearTimeout(timeoutId);
      console.error('Komple sync hatası:', err);
      if (err.name === 'AbortError') {
        setError('Bağlantı zaman aşımına uğradı. Sunucu erişilebilir değil olabilir.');
      } else {
        setError(err.message);
      }
    } finally {
      setSyncing(false);
    }
  };

  // Seçili kaynaktan senkronize et
  const syncFaturalar = async () => {
    setSyncing(true);
    setError(null);
    
    // AbortController ile timeout ekle
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 saniye timeout
    
    try {
      const response = await fetch(`${API_URL}/api/faturalar/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sirket: selectedSirket,
          kaynak: currentKaynak.kaynak,
          tur: currentKaynak.tur,
          faturaTipi: currentKaynak.faturaTipi,
          baslangicTarihi: dateFilter.baslangic,
          bitisTarihi: dateFilter.bitis || new Date().toISOString().split('T')[0]
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // 503 hatası - Netsis'e bağlanılamadı
      if (response.status === 503) {
        throw new Error('Netsis sunucusuna bağlanılamadı. Sunucu erişimi veya firewall ayarlarını kontrol edin.');
      }
      
      // Diğer hata durumları
      if (!response.ok) {
        let errorMsg = `Sunucu hatası (${response.status})`;
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorMsg;
        } catch (e) {
          // JSON parse edilemezse default mesajı kullan
        }
        throw new Error(errorMsg);
      }
      
      // Başarılı yanıtı parse et
      let data;
      try {
        data = await response.json();
      } catch (e) {
        throw new Error('Sunucudan geçersiz yanıt alındı');
      }
      
      // Detaylı mesaj göster
      const msg = data.sonSyncTarihi 
        ? `✅ Akıllı Sync: ${data.sonSyncTarihi}'den itibaren çekildi\n${data.yeniKayit || 0} yeni, ${data.guncellenen || 0} güncellendi\n📦 Hafızada toplam: ${data.hafizadakiToplam || 0} fatura`
        : `✅ ${data.yeniKayit || 0} yeni fatura, ${data.guncellenen || 0} güncellendi\n📦 Hafızada toplam: ${data.hafizadakiToplam || 0} fatura`;
      alert(msg);
      
      fetchFaturalar();
      fetchSonSync();
      fetchHafizaOzet();
    } catch (err) {
      clearTimeout(timeoutId);
      console.error('Sync hatası:', err);
      if (err.name === 'AbortError') {
        setError('Bağlantı zaman aşımına uğradı. Sunucu erişilebilir değil olabilir.');
      } else {
        setError(err.message);
      }
    } finally {
      setSyncing(false);
    }
  };

  // Hafıza özetini getir
  const fetchHafizaOzet = async () => {
    try {
      const response = await fetch(`${API_URL}/api/faturalar/hafiza-ozet?sirket=${selectedSirket}`);
      if (response.ok) {
        const text = await response.text();
        try {
          const data = JSON.parse(text);
          setHafizaOzet(data);
        } catch (e) {
          // ignore
        }
      }
    } catch (err) {
      console.error('Hafıza özeti alınamadı:', err);
    }
  };

  // Günlük özeti getir (seçili tarih aralığı için tüm kaynakların toplam tutarları)
  const fetchGunlukOzet = async () => {
    try {
      const bitisTarih = dateFilter.bitis || dateFilter.baslangic;
      const response = await fetch(`${API_URL}/api/faturalar/gunluk-ozet?sirket=${selectedSirket}&baslangic=${dateFilter.baslangic}&bitis=${bitisTarih}`);
      if (response.ok) {
        const text = await response.text();
        try {
          const data = JSON.parse(text);
          setGunlukOzet(data);
        } catch (e) {
          // ignore
        }
      }
    } catch (err) {
      console.error('Günlük özet alınamadı:', err);
    }
  };

  // API durumunu kontrol et
  const checkApiStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/faturalar/api-status`);
      if (response.ok) {
        const text = await response.text();
        try {
          const data = JSON.parse(text);
          setApiStatus(data);
        } catch (e) {
          // ignore
        }
      }
    } catch (err) {
      console.error('API durum kontrolü başarısız:', err);
    }
  };

  // İlk yüklemede
  useEffect(() => {
    fetchFaturalar();
    checkApiStatus();
    fetchSonSync();
    fetchHafizaOzet();
    fetchGunlukOzet();
  }, [selectedSirket, selectedKaynak, dateFilter.baslangic, dateFilter.bitis]);

  // Otomatik yenileme
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchFaturalar(false); // Loading göstermeden yenile
      fetchSonSync();
    }, AUTO_REFRESH_INTERVAL);
    
    return () => clearInterval(interval);
  }, [autoRefresh, fetchFaturalar]);

  // Arama filtresi
  const filteredFaturalar = faturalar.filter(fatura => {
    const searchLower = searchTerm.toLowerCase();
    return (
      fatura.faturaNo?.toLowerCase().includes(searchLower) ||
      fatura.firmaAdi?.toLowerCase().includes(searchLower) ||
      fatura.vergiNo?.toLowerCase().includes(searchLower)
    );
  });

  // Para formatla
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(value || 0);
  };

  // Tarih formatla
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  // Saat formatla
  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('tr-TR');
  };

  const currentSirket = SIRKETLER.find(s => s.id === selectedSirket);
  const isApiConnected = apiStatus[selectedSirket]?.[currentKaynak.kaynak] || false;

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-xl md:text-3xl font-bold text-textPrimary flex items-center gap-2 md:gap-3">
            <FileText className="w-6 h-6 md:w-8 md:h-8 text-primary" />
            Fatura Yönetimi
          </h1>
          <p className="text-xs md:text-sm text-textSecondary mt-1">E-Fatura takibi - EDM & Netsis</p>
        </div>
        
        {/* Mobil için butonlar - yatay scroll */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap">
          {/* Son Senkronizasyon */}
          {sonSync && (
            <div className="flex items-center gap-2 text-xs text-textSecondary bg-surface px-2 py-1.5 rounded-lg border border-border whitespace-nowrap flex-shrink-0">
              <Clock className="w-3 h-3" />
              <span className="hidden sm:inline">Son sync:</span> {formatTime(sonSync).split(' ')[1]}
            </div>
          )}
          
          {/* EDM-Netsis Karşılaştır Butonu */}
          <Button
            onClick={() => setShowKarsilastirma(true)}
            variant="outline"
            size="sm"
            className="border-purple-500 text-purple-600 hover:bg-purple-500 hover:text-white whitespace-nowrap flex-shrink-0"
            data-testid="compare-btn"
          >
            <Database className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">EDM - Netsis</span> Karşılaştır
          </Button>
          
          {/* Otomatik Yenileme Toggle */}
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
              autoRefresh 
                ? 'bg-green-100 text-green-700 border border-green-300' 
                : 'bg-gray-100 text-gray-600 border border-gray-300'
            }`}
            title={autoRefresh ? 'Otomatik yenileme açık (30 sn)' : 'Otomatik yenileme kapalı'}
          >
            <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} style={{ animationDuration: '3s' }} />
            {autoRefresh ? 'Otomatik' : 'Manuel'}
          </button>
          
          {/* Tümünü Senkronize Et */}
          <Button
            onClick={syncAllFaturalar}
            disabled={syncingAll}
            variant="outline"
            size="sm"
            className="border-primary text-primary hover:bg-primary hover:text-white whitespace-nowrap flex-shrink-0"
            data-testid="sync-all-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${syncingAll ? 'animate-spin' : ''}`} />
            {syncingAll ? 'Sync...' : <><span className="hidden sm:inline">Tümünü</span> Sync</>}
          </Button>
        </div>
      </div>

      {/* Şirket Seçimi */}
      <div className="bg-surface rounded-xl shadow-sm border border-border p-3 md:p-4">
        <h3 className="text-xs md:text-sm font-medium text-textSecondary mb-2 md:mb-3">Şirket Seçin</h3>
        <div className="flex flex-wrap gap-2">
          {SIRKETLER.map(sirket => (
            <button
              key={sirket.id}
              onClick={() => setSelectedSirket(sirket.id)}
              className={`px-3 py-2 md:px-4 md:py-3 rounded-lg text-sm md:text-base font-medium transition-all flex items-center gap-1 md:gap-2 ${
                selectedSirket === sirket.id
                  ? `${sirket.color} text-white shadow-lg scale-105`
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid={`sirket-btn-${sirket.id}`}
            >
              <Building2 className="w-4 h-4 md:w-5 md:h-5" />
              <span className="hidden sm:inline">{sirket.ad}</span>
              <span className="sm:hidden">{sirket.ad.split(' ')[0]}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Fatura Kaynağı ve Türü Seçimi */}
      <div className="bg-surface rounded-xl shadow-sm border border-border p-3 md:p-4">
        <h3 className="text-xs md:text-sm font-medium text-textSecondary mb-2 md:mb-3">Fatura Kaynağı</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-3">
          {FATURA_KAYNAKLARI.map(kaynak => {
            const Icon = kaynak.icon;
            const isActive = selectedKaynak === kaynak.id;
            const isConnected = apiStatus[selectedSirket]?.[kaynak.kaynak] || false;
            // Günlük toplam tutar
            const kaynakKey = `${kaynak.kaynak}_${kaynak.faturaTipi}`;
            const gunlukData = gunlukOzet[kaynakKey];
            const toplamTutar = gunlukData?.toplamTutar || 0;
            
            return (
              <button
                key={kaynak.id}
                onClick={() => setSelectedKaynak(kaynak.id)}
                className={`p-2 md:p-4 rounded-xl text-xs md:text-sm font-medium transition-all flex flex-col items-center gap-1 md:gap-2 relative ${
                  isActive
                    ? `${kaynak.color} text-white shadow-lg`
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
                data-testid={`kaynak-btn-${kaynak.id}`}
              >
                {/* API Durum Göstergesi */}
                <div className={`absolute top-1 right-1 md:top-2 md:right-2 w-2 h-2 rounded-full ${
                  isConnected ? 'bg-green-400' : 'bg-red-400'
                }`} title={isConnected ? 'API Bağlı' : 'API Bağlı Değil'} />
                
                <Icon className="w-5 h-5 md:w-6 md:h-6" />
                <span className="font-semibold text-center leading-tight">{kaynak.label}</span>
                {/* Günlük Toplam Tutar */}
                <span className={`text-xs md:text-sm font-bold ${isActive ? 'text-white' : 'text-gray-800'}`}>
                  {toplamTutar > 0 ? formatCurrency(toplamTutar) : '-'}
                </span>
                <span className={`text-xs ${isActive ? 'text-white/80' : 'text-gray-500'}`}>
                  {kaynak.tur === 'gelen' ? (
                    <span className="flex items-center gap-1">
                      <ArrowDownCircle className="w-3 h-3" /> Gelen
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <ArrowUpCircle className="w-3 h-3" /> Giden
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Filtreler ve Aksiyonlar */}
      <div className="bg-surface rounded-xl shadow-sm border border-border p-3 md:p-4">
        <div className="flex flex-col gap-3">
          {/* Tarih ve Arama - Mobilde üst üste */}
          <div className="flex flex-col sm:flex-row gap-2">
            {/* Tarih Filtresi */}
            <div className="flex items-center gap-2 flex-1">
              <Calendar className="w-4 h-4 text-gray-500 hidden sm:block" />
              <Input
                type="date"
                value={dateFilter.baslangic}
                onChange={(e) => setDateFilter({ ...dateFilter, baslangic: e.target.value })}
                className="flex-1 text-sm"
                data-testid="baslangic-tarih"
              />
              <span className="text-gray-500">-</span>
              <Input
                type="date"
                value={dateFilter.bitis}
                onChange={(e) => setDateFilter({ ...dateFilter, bitis: e.target.value })}
                className="flex-1 text-sm"
                data-testid="bitis-tarih"
              />
            </div>
          </div>
          
          {/* Arama ve Butonlar */}
          <div className="flex flex-col sm:flex-row gap-2">
            {/* Arama */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Fatura no, firma ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full text-sm"
                data-testid="fatura-search"
              />
            </div>

            {/* Butonlar */}
            <div className="flex gap-2">
              <Button onClick={fetchFaturalar} variant="outline" size="sm" className="flex-1 sm:flex-none">
                <Filter className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Filtrele</span>
              </Button>
              
              {/* Senkronizasyon */}
              <Button
                onClick={currentKaynak.kaynak === 'edm' ? syncKomple : syncFaturalar}
                disabled={syncing || !isApiConnected}
                size="sm"
                className={`${currentKaynak.kaynak === 'edm' ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700' : currentKaynak.color + ' hover:opacity-90'} text-white flex-1 sm:flex-none`}
                data-testid="sync-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Çekiliyor...' : currentKaynak.kaynak === 'edm' ? 'ERP+EDM\'den Çek' : <><span className="hidden sm:inline">{currentKaynak.kaynak.toUpperCase()}'den</span> Çek</>}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Hata Mesajı */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 md:p-4 flex items-center gap-2 md:gap-3">
          <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-red-500 flex-shrink-0" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      {/* İstatistikler - Mobilde 2x3 grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 md:gap-4">
        <div className={`${currentSirket?.color} rounded-xl p-3 md:p-4 text-white col-span-2 sm:col-span-1`}>
          <div className="text-xs md:text-sm opacity-90">{currentSirket?.ad}</div>
          <div className="text-base md:text-xl font-bold mt-1">{currentKaynak.label}</div>
        </div>
        <div className="bg-surface rounded-xl p-3 md:p-4 border border-border">
          <div className="text-xs md:text-sm text-textSecondary">Görüntülenen</div>
          <div className="text-lg md:text-2xl font-bold text-textPrimary mt-1">{stats.toplam}</div>
        </div>
        <div className="bg-green-50 rounded-xl p-3 md:p-4 border border-green-200">
          <div className="text-xs md:text-sm text-green-700">📦 Hafızada</div>
          <div className="text-lg md:text-2xl font-bold text-green-600 mt-1">
            {hafizaOzet.find(h => h.kaynak === currentKaynak.kaynak && h.faturaTipi === currentKaynak.faturaTipi)?.faturaSayisi || 0}
          </div>
        </div>
        <div className="bg-surface rounded-xl p-3 md:p-4 border border-border">
          <div className="text-xs md:text-sm text-textSecondary">Toplam Tutar</div>
          <div className="text-sm md:text-2xl font-bold text-textPrimary mt-1">{formatCurrency(stats.toplamTutar)}</div>
        </div>
        <div className="bg-surface rounded-xl p-3 md:p-4 border border-border">
          <div className="text-xs md:text-sm text-textSecondary">API</div>
          <div className={`text-sm md:text-lg font-bold mt-1 flex items-center gap-2 ${isApiConnected ? 'text-green-600' : 'text-red-600'}`}>
            <div className={`w-2 h-2 md:w-3 md:h-3 rounded-full ${isApiConnected ? 'bg-green-500' : 'bg-red-500'}`} />
            {isApiConnected ? 'Bağlı' : 'Yok'}
          </div>
        </div>
      </div>

      {/* Fatura Tablosu - Mobilde yatay scroll */}
      <div className="bg-surface rounded-xl shadow-sm border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Fatura No</th>
                <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Tarih</th>
                <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                  {currentKaynak.tur === 'gelen' ? 'Satıcı' : 'Alıcı'}
                </th>
                <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap hidden md:table-cell">Vergi No</th>
                <th className="px-2 md:px-4 py-2 md:py-3 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap hidden lg:table-cell">Matrah</th>
                <th className="px-2 md:px-4 py-2 md:py-3 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap hidden lg:table-cell">KDV</th>
                <th className="px-2 md:px-4 py-2 md:py-3 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Toplam</th>
                <th className="px-2 md:px-4 py-2 md:py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap hidden sm:table-cell">Durum</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Görüntüle</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-border">
              {loading ? (
                <tr>
                  <td colSpan="10" className="px-4 py-8 text-center text-gray-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Yükleniyor...
                  </td>
                </tr>
              ) : filteredFaturalar.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-4 py-8 text-center text-gray-500">
                    <FileText className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-2 text-gray-300" />
                    <span className="text-xs md:text-sm">
                      {isApiConnected 
                        ? `Fatura bulunamadı. ${currentKaynak.kaynak.toUpperCase()}'den çekin.`
                        : `${currentKaynak.kaynak.toUpperCase()} API bağlantısı yok.`}
                    </span>
                  </td>
                </tr>
              ) : (
                filteredFaturalar.map((fatura, index) => (
                  <tr key={fatura.id || index} className="hover:bg-gray-50">
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm font-medium text-textPrimary">
                      <span className="truncate max-w-[100px] md:max-w-none block">{fatura.faturaNo}</span>
                    </td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm text-textSecondary whitespace-nowrap">
                      {formatDate(fatura.tarih)}
                    </td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm text-textPrimary">
                      <span className="truncate max-w-[120px] md:max-w-[200px] block">{fatura.firmaAdi}</span>
                    </td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm text-textSecondary hidden md:table-cell">
                      {fatura.vergiNo}
                    </td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm text-right text-textPrimary hidden lg:table-cell">
                      {formatCurrency(fatura.matrah)}
                    </td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm text-right text-textSecondary hidden lg:table-cell">
                      {formatCurrency(fatura.kdvTutar)}
                    </td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm text-right font-medium text-textPrimary whitespace-nowrap">
                      {formatCurrency(fatura.toplamTutar)}
                    </td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-center hidden sm:table-cell">
                      <span className={`px-1.5 md:px-2 py-0.5 md:py-1 text-xs font-medium rounded-full ${
                        fatura.durum === 'onaylandi' 
                          ? 'bg-green-100 text-green-800'
                          : fatura.durum === 'beklemede'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {fatura.durum === 'onaylandi' ? '✓' : 
                         fatura.durum === 'beklemede' ? '⏳' : '-'}
                      </span>
                    </td>
                    <td className="px-2 md:px-4 py-2 md:py-3 text-center">
                      <button
                        onClick={() => viewFatura(fatura)}
                        className="p-1.5 md:p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                        title="Faturayı Görüntüle"
                        data-testid={`view-fatura-${fatura.id}`}
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Fatura Görüntüleme Modal */}
      {viewingFatura && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 md:p-4" onClick={closeFaturaModal}>
          <div 
            className="bg-white rounded-xl md:rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gray-50 px-3 md:px-6 py-3 md:py-4 border-b flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h2 className="text-base md:text-xl font-bold text-gray-800 truncate">Fatura Görüntüleme</h2>
                <p className="text-xs md:text-sm text-gray-500 truncate">{viewingFatura.faturaNo} - {formatDate(viewingFatura.tarih)}</p>
              </div>
              <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
                <Button
                  onClick={refreshFatura}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-3"
                  title="Yeniden Çek (Cache'i Yenile)"
                  disabled={loadingFatura}
                >
                  <RefreshCw className={`w-3 h-3 md:w-4 md:h-4 ${loadingFatura ? 'animate-spin' : ''}`} />
                  <span className="hidden md:inline">Yenile</span>
                </Button>
                <Button
                  onClick={() => downloadPdf(viewingFatura)}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-3 bg-blue-50 hover:bg-blue-100 text-blue-600"
                >
                  <Download className="w-3 h-3 md:w-4 md:h-4" />
                  <span className="hidden md:inline">PDF İndir</span>
                </Button>
                <Button
                  onClick={printFatura}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-3"
                >
                  <Printer className="w-3 h-3 md:w-4 md:h-4" />
                  <span className="hidden md:inline">Yazdır</span>
                </Button>
                <button
                  onClick={closeFaturaModal}
                  className="p-1.5 md:p-2 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4 md:w-5 md:h-5" />
                </button>
              </div>
            </div>
            
            {/* Modal Content - PDF veya A4 Preview */}
            <div className="flex-1 overflow-auto p-2 md:p-6 bg-gray-100">
              {loadingFatura ? (
                <div className="flex flex-col items-center justify-center h-64 gap-3">
                  <RefreshCw className="w-8 h-8 animate-spin text-primary" />
                  <p className="text-sm text-gray-500">Fatura yükleniyor...</p>
                </div>
              ) : faturaPdfUrl ? (
                /* PDF Görüntüleme - Gerçek Fatura (tam ekran, sol panel gizli, sayfa sığdır) */
                <div className="w-full h-full" style={{ minHeight: '85vh' }}>
                  <object
                    data={`${faturaPdfUrl}#navpanes=0&toolbar=1&view=Fit&zoom=page-fit`}
                    type="application/pdf"
                    className="w-full h-full"
                    style={{ minHeight: '85vh' }}
                  >
                    {/* PDF görüntülenemezse alternatif */}
                    <div className="flex flex-col items-center justify-center h-64 gap-4 bg-gray-50 rounded-lg">
                      <FileText className="w-16 h-16 text-gray-400" />
                      <p className="text-gray-600">PDF tarayıcıda görüntülenemiyor</p>
                      <a 
                        href={faturaPdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        PDF'i Yeni Sekmede Aç
                      </a>
                    </div>
                  </object>
                </div>
              ) : (
                <div 
                  className="bg-white shadow-lg mx-auto overflow-auto"
                  style={{ 
                    width: '100%',
                    maxWidth: '210mm',
                    minHeight: '200px'
                  }}
                >
                  {faturaHtml ? (
                    <iframe
                      srcDoc={faturaHtml}
                      className="w-full border-0"
                      style={{ minHeight: '80vh', height: '100%' }}
                      title="Fatura"
                    />
                  ) : (
                    <iframe
                      srcDoc={generateFaturaHtml(viewingFatura)}
                      className="w-full border-0"
                      style={{ minHeight: '80vh', height: '100%' }}
                      title="Fatura"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* EDM - Netsis Karşılaştırma Modal */}
      {showKarsilastirma && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowKarsilastirma(false)}>
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-500 to-indigo-600 px-6 py-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <Database className="w-6 h-6" />
                    EDM - Netsis Fatura Karşılaştırma
                  </h2>
                  <p className="text-purple-100 text-sm mt-1">İki sistem arasındaki fatura farklılıklarını tespit edin</p>
                </div>
                <button
                  onClick={() => setShowKarsilastirma(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Filtreler */}
            <div className="p-4 bg-gray-50 border-b flex flex-wrap items-end gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Başlangıç Tarihi</label>
                <Input
                  type="date"
                  value={karsilastirmaTarih.baslangic}
                  onChange={(e) => setKarsilastirmaTarih(prev => ({ ...prev, baslangic: e.target.value }))}
                  className="w-40"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Bitiş Tarihi</label>
                <Input
                  type="date"
                  value={karsilastirmaTarih.bitis}
                  onChange={(e) => setKarsilastirmaTarih(prev => ({ ...prev, bitis: e.target.value }))}
                  className="w-40"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Fatura Tipi</label>
                <select
                  value={karsilastirmaTipi}
                  onChange={(e) => setKarsilastirmaTipi(e.target.value)}
                  className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="satis">Satış</option>
                  <option value="alis">Alış</option>
                  <option value="iade_giden">İade Giden</option>
                  <option value="iade_gelen">İade Gelen</option>
                </select>
              </div>
              <Button
                onClick={karsilastir}
                disabled={karsilastirmaLoading}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {karsilastirmaLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Karşılaştırılıyor...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Karşılaştır
                  </>
                )}
              </Button>
              
              {/* Arama Kutusu */}
              {karsilastirmaSonuc && (
                <div className="ml-auto">
                  <Input
                    type="text"
                    placeholder="Fatura No, Firma, VKN/TC ara..."
                    value={karsilastirmaArama}
                    onChange={(e) => setKarsilastirmaArama(e.target.value)}
                    className="w-64"
                  />
                </div>
              )}
            </div>

            {/* Sonuçlar */}
            <div className="flex-1 overflow-auto p-4">
              {karsilastirmaSonuc ? (
                <div className="space-y-6">
                  {/* Toplam Fark Özet */}
                  <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-4 text-white">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold">{karsilastirmaSonuc.ozet.edmToplam}</div>
                        <div className="text-sm opacity-90">EDM Fatura</div>
                        <div className="text-lg font-semibold mt-1">
                          {(karsilastirmaSonuc.ozet.edmToplamTutar || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺
                        </div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold">{karsilastirmaSonuc.ozet.netsisToplam}</div>
                        <div className="text-sm opacity-90">Netsis Fatura</div>
                        <div className="text-lg font-semibold mt-1">
                          {(karsilastirmaSonuc.ozet.netsisToplamTutar || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺
                        </div>
                      </div>
                      <div className="bg-white/20 rounded-lg p-2">
                        <div className="text-2xl font-bold text-yellow-300">
                          {(karsilastirmaSonuc.ozet.toplamFark || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺
                        </div>
                        <div className="text-sm opacity-90">TOPLAM FARK</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Özet Kartları */}
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                    <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
                      <div className="text-xl font-bold text-green-600">{karsilastirmaSonuc.ozet.eslesen || 0}</div>
                      <div className="text-xs text-green-700">Eşleşen</div>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-center">
                      <div className="text-xl font-bold text-yellow-600">{karsilastirmaSonuc.ozet.farkli || 0}</div>
                      <div className="text-xs text-yellow-700">Alan Farkı</div>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                      <div className="text-xl font-bold text-amber-600">{karsilastirmaSonuc.ozet.tarihFarkli || 0}</div>
                      <div className="text-xs text-amber-700">Tarih Farkı</div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
                      <div className="text-xl font-bold text-red-600">{karsilastirmaSonuc.ozet.tutarFarkli || 0}</div>
                      <div className="text-xs text-red-700">Tutar Farkı</div>
                      <div className="text-xs text-red-500 font-semibold">
                        {(karsilastirmaSonuc.ozet.tutarFarkToplam || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺
                      </div>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-center">
                      <div className="text-xl font-bold text-orange-600">{karsilastirmaSonuc.ozet.sadeceEdm || 0}</div>
                      <div className="text-xs text-orange-700">Sadece EDM</div>
                      <div className="text-xs text-orange-500 font-semibold">
                        {(karsilastirmaSonuc.ozet.sadeceEdmToplam || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺
                      </div>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-center">
                      <div className="text-xl font-bold text-purple-600">{karsilastirmaSonuc.ozet.sadeceNetsis || 0}</div>
                      <div className="text-xs text-purple-700">Sadece Netsis</div>
                      <div className="text-xs text-purple-500 font-semibold">
                        {(karsilastirmaSonuc.ozet.sadeceNetsisToplam || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺
                      </div>
                    </div>
                  </div>

                  {/* Uyarı: Veri Eksik */}
                  {(karsilastirmaSonuc.ozet.edmToplam === 0 || karsilastirmaSonuc.ozet.netsisToplam === 0) && (
                    <div className="bg-orange-100 border-2 border-orange-400 rounded-xl p-4 flex items-start gap-3">
                      <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-bold text-orange-800">⚠️ Karşılaştırma İçin Veri Eksik!</h4>
                        <p className="text-sm text-orange-700 mt-1">
                          {karsilastirmaSonuc.ozet.edmToplam === 0 && karsilastirmaSonuc.ozet.netsisToplam === 0 
                            ? 'Hem EDM hem Netsis\'te bu tarih aralığında fatura bulunamadı. Lütfen önce her iki sistemden de faturaları senkronize edin.'
                            : karsilastirmaSonuc.ozet.edmToplam === 0 
                              ? 'EDM\'de fatura bulunamadı. Lütfen önce EDM\'den faturaları senkronize edin.'
                              : 'Netsis\'te fatura bulunamadı. Lütfen önce Netsis\'ten faturaları senkronize edin (Fatura sayfasından "Netsis Satış" seçip "Netsis\'den Çek" butonuna tıklayın).'
                          }
                        </p>
                        <p className="text-xs text-orange-600 mt-2">
                          💡 Karşılaştırma yapabilmek için her iki sistemde de (EDM ve Netsis) aynı tarih aralığında fatura olması gerekir.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Eşleşip Farkı Olanlar */}
                  {karsilastirmaSonuc.farklilar && karsilastirmaSonuc.farklilar.length > 0 && (() => {
                    const aramaLower = karsilastirmaArama.toLowerCase();
                    const filtreliFarklilar = karsilastirmaSonuc.farklilar.filter(item => {
                      if (!karsilastirmaArama) return true;
                      return (
                        item.edm?.faturaNo?.toLowerCase().includes(aramaLower) ||
                        item.netsis?.faturaNo?.toLowerCase().includes(aramaLower) ||
                        item.edm?.firmaAdi?.toLowerCase().includes(aramaLower) ||
                        item.netsis?.firmaAdi?.toLowerCase().includes(aramaLower) ||
                        item.edm?.vergiNo?.toLowerCase().includes(aramaLower) ||
                        item.netsis?.vergiNo?.toLowerCase().includes(aramaLower)
                      );
                    });
                    return filtreliFarklilar.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl overflow-hidden">
                      <div className="bg-yellow-100 px-4 py-3 border-b border-yellow-200 flex justify-between items-center">
                        <div>
                          <h3 className="font-bold text-yellow-800 flex items-center gap-2">
                            <AlertCircle className="w-5 h-5" />
                            Eşleşti Ama Bazı Alanlar Farklı ({filtreliFarklilar.length})
                          </h3>
                          <p className="text-xs text-yellow-600 mt-1">Tarih + Tutar eşleşti, diğer alanlar farklı</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => {
                            const columns = [
                              { header: 'Tarih', accessor: (r) => r.tarih },
                              { header: 'EDM Fatura No', accessor: (r) => r.edm?.faturaNo },
                              { header: 'Netsis Fatura No', accessor: (r) => r.netsis?.faturaNo },
                              { header: 'Cari Adı', accessor: (r) => r.edm?.firmaAdi || r.netsis?.firmaAdi },
                              { header: 'VKN/TC', accessor: (r) => r.edm?.vergiNo || r.netsis?.vergiNo },
                              { header: 'Tutar', accessor: (r) => r.edm?.toplamTutar || r.netsis?.toplamTutar },
                              { header: 'Farklı Alanlar', accessor: (r) => r.farklar?.map(f => `${f.alan}: EDM(${f.edm}) / Netsis(${f.netsis})`).join('; ') }
                            ];
                            exportToExcel(filtreliFarklilar, columns, 'Alan_Farkli_Faturalar');
                          }}>
                            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => {
                            const columns = [
                              { header: 'Tarih', accessor: (r) => r.tarih },
                              { header: 'EDM Fatura No', accessor: (r) => r.edm?.faturaNo },
                              { header: 'Netsis Fatura No', accessor: (r) => r.netsis?.faturaNo },
                              { header: 'Cari Adı', accessor: (r) => r.edm?.firmaAdi || r.netsis?.firmaAdi },
                              { header: 'Tutar', accessor: (r) => r.edm?.toplamTutar || r.netsis?.toplamTutar }
                            ];
                            exportToPDF(filtreliFarklilar, columns, 'Eşleşti Ama Bazı Alanlar Farklı', 'Alan_Farkli_Faturalar');
                          }}>
                            <Printer className="w-4 h-4 mr-1" /> PDF
                          </Button>
                        </div>
                      </div>
                      <div className="overflow-x-auto max-h-72">
                        <table className="w-full text-sm">
                          <thead className="bg-yellow-100/50 sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-left">Tarih</th>
                              <th className="px-3 py-2 text-left">Cari Adı</th>
                              <th className="px-3 py-2 text-left">Tutar</th>
                              <th className="px-3 py-2 text-center">Farklı Alan</th>
                              <th className="px-3 py-2 text-left text-blue-700">EDM Değeri</th>
                              <th className="px-3 py-2 text-left text-indigo-700">Netsis Değeri</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filtreliFarklilar.map((item, idx) => (
                              item.farklar.map((fark, fIdx) => (
                                <tr key={`${idx}-${fIdx}`} className="border-t border-yellow-200 hover:bg-yellow-100/30">
                                  {fIdx === 0 && (
                                    <>
                                      <td className="px-3 py-2" rowSpan={item.farklar.length}>{item.tarih}</td>
                                      <td className="px-3 py-2 text-xs" rowSpan={item.farklar.length}>
                                        {item.edm?.firmaAdi?.slice(0, 25) || item.netsis?.firmaAdi?.slice(0, 25)}
                                      </td>
                                      <td className="px-3 py-2 font-medium" rowSpan={item.farklar.length}>
                                        {item.toplamTutar?.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺
                                      </td>
                                    </>
                                  )}
                                  <td className="px-3 py-2 text-center">
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                                      fark.alan === 'faturaNo' ? 'bg-orange-200 text-orange-800' :
                                      fark.alan === 'vergiNo' ? 'bg-purple-200 text-purple-800' :
                                      'bg-pink-200 text-pink-800'
                                    }`}>
                                      {fark.alan === 'faturaNo' ? 'Fatura No' :
                                       fark.alan === 'vergiNo' ? 'VKN' :
                                       fark.alan === 'kdvTutar' ? 'KDV' : fark.alan}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 font-mono text-blue-700 text-xs">
                                    {typeof fark.edm === 'number' ? fark.edm.toLocaleString('tr-TR', {minimumFractionDigits: 2}) : fark.edm}
                                  </td>
                                  <td className="px-3 py-2 font-mono text-indigo-700 text-xs">
                                    {typeof fark.netsis === 'number' ? fark.netsis.toLocaleString('tr-TR', {minimumFractionDigits: 2}) : fark.netsis}
                                  </td>
                                </tr>
                              ))
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )})()}

                  {/* 🟡 Tarih Farklı Olanlar */}
                  {karsilastirmaSonuc.tarihFarkli && karsilastirmaSonuc.tarihFarkli.length > 0 && (() => {
                    const aramaLower = karsilastirmaArama.toLowerCase();
                    const filtreliTarih = karsilastirmaSonuc.tarihFarkli.filter(item => {
                      if (!karsilastirmaArama) return true;
                      return (
                        item.edm?.faturaNo?.toLowerCase().includes(aramaLower) ||
                        item.netsis?.faturaNo?.toLowerCase().includes(aramaLower) ||
                        item.edm?.firmaAdi?.toLowerCase().includes(aramaLower) ||
                        item.netsis?.firmaAdi?.toLowerCase().includes(aramaLower) ||
                        item.edm?.vergiNo?.toLowerCase().includes(aramaLower) ||
                        item.netsis?.vergiNo?.toLowerCase().includes(aramaLower)
                      );
                    });
                    return filtreliTarih.length > 0 && (
                    <div className="bg-amber-50 border border-amber-300 rounded-xl overflow-hidden">
                      <div className="bg-amber-200 px-4 py-3 border-b border-amber-300">
                        <h3 className="font-bold text-amber-900 flex items-center gap-2">
                          <Calendar className="w-5 h-5" />
                          🟡 TARİH FARKLI - VKN + Tutar Eşleşti ({filtreliTarih.length})
                        </h3>
                        <p className="text-xs text-amber-700 mt-1">Aynı VKN ve tutar, farklı tarih</p>
                      </div>
                      <div className="overflow-x-auto max-h-60">
                        <table className="w-full text-sm">
                          <thead className="bg-amber-100 sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-left">Firma / VKN</th>
                              <th className="px-3 py-2 text-center text-blue-700">EDM Tarih</th>
                              <th className="px-3 py-2 text-center text-indigo-700">Netsis Tarih</th>
                              <th className="px-3 py-2 text-left text-blue-700">EDM Fatura No</th>
                              <th className="px-3 py-2 text-left text-indigo-700">Netsis Fatura No</th>
                              <th className="px-3 py-2 text-right">Tutar</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filtreliTarih.map((item, idx) => (
                              <tr key={idx} className="border-t border-amber-200 hover:bg-amber-100/50">
                                <td className="px-3 py-2">
                                  <div className="text-xs font-medium">{item.edm?.firmaAdi?.slice(0, 25) || item.netsis?.firmaAdi?.slice(0, 25)}</div>
                                  <div className="text-xs text-gray-500 font-mono">{item.edm?.vergiNo || item.netsis?.vergiNo}</div>
                                </td>
                                <td className="px-3 py-2 text-center font-medium text-blue-700 bg-blue-50">{item.edm?.tarih}</td>
                                <td className="px-3 py-2 text-center font-medium text-indigo-700 bg-indigo-50">{item.netsis?.tarih}</td>
                                <td className="px-3 py-2 text-xs text-blue-700">{item.edm?.faturaNo}</td>
                                <td className="px-3 py-2 text-xs text-indigo-700">{item.netsis?.faturaNo}</td>
                                <td className="px-3 py-2 text-right font-mono">
                                  {item.edm?.toplamTutar?.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )})()}

                  {/* 🔴 Tutar Farklı Olanlar */}
                  {karsilastirmaSonuc.tutarFarkli && karsilastirmaSonuc.tutarFarkli.length > 0 && (() => {
                    const aramaLower = karsilastirmaArama.toLowerCase();
                    const filtreliTutar = karsilastirmaSonuc.tutarFarkli.filter(item => {
                      if (!karsilastirmaArama) return true;
                      return (
                        item.edm?.faturaNo?.toLowerCase().includes(aramaLower) ||
                        item.netsis?.faturaNo?.toLowerCase().includes(aramaLower) ||
                        item.edm?.firmaAdi?.toLowerCase().includes(aramaLower) ||
                        item.netsis?.firmaAdi?.toLowerCase().includes(aramaLower) ||
                        item.edm?.vergiNo?.toLowerCase().includes(aramaLower) ||
                        item.netsis?.vergiNo?.toLowerCase().includes(aramaLower)
                      );
                    });
                    return filtreliTutar.length > 0 && (
                    <div className="bg-red-50 border border-red-300 rounded-xl overflow-hidden">
                      <div className="bg-red-200 px-4 py-3 border-b border-red-300 flex justify-between items-center">
                        <div>
                          <h3 className="font-bold text-red-900 flex items-center gap-2">
                            <AlertCircle className="w-5 h-5" />
                            🔴 TUTAR FARKLI - VKN + Tarih Eşleşti ({filtreliTutar.length})
                          </h3>
                          <p className="text-xs text-red-700 mt-1">Aynı VKN ve tarih, farklı tutar - Toplam Fark: {filtreliTutar.reduce((t, i) => t + Math.abs(i.fark || 0), 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="h-8 text-xs bg-white" onClick={() => {
                            const columns = [
                              { header: 'Firma Adı', accessor: (r) => r.edm?.firmaAdi || r.netsis?.firmaAdi },
                              { header: 'VKN/TC', accessor: (r) => r.edm?.vergiNo || r.netsis?.vergiNo },
                              { header: 'Tarih', accessor: (r) => r.edm?.tarih || r.netsis?.tarih },
                              { header: 'EDM Tutar', accessor: (r) => r.edm?.toplamTutar },
                              { header: 'Netsis Tutar', accessor: (r) => r.netsis?.toplamTutar },
                              { header: 'FARK', accessor: (r) => r.fark },
                              { header: 'EDM Fatura No', accessor: (r) => r.edm?.faturaNo },
                              { header: 'Netsis Fatura No', accessor: (r) => r.netsis?.faturaNo }
                            ];
                            exportToExcel(filtreliTutar, columns, 'Tutar_Farkli_Faturalar');
                          }}>
                            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 text-xs bg-white" onClick={() => {
                            const columns = [
                              { header: 'Firma', accessor: (r) => r.edm?.firmaAdi || r.netsis?.firmaAdi },
                              { header: 'VKN', accessor: (r) => r.edm?.vergiNo || r.netsis?.vergiNo },
                              { header: 'Tarih', accessor: (r) => r.edm?.tarih || r.netsis?.tarih },
                              { header: 'EDM Tutar', accessor: (r) => r.edm?.toplamTutar },
                              { header: 'Netsis Tutar', accessor: (r) => r.netsis?.toplamTutar },
                              { header: 'FARK', accessor: (r) => r.fark }
                            ];
                            exportToPDF(filtreliTutar, columns, 'Tutar Farklı Faturalar', 'Tutar_Farkli_Faturalar');
                          }}>
                            <Printer className="w-4 h-4 mr-1" /> PDF
                          </Button>
                        </div>
                      </div>
                      <div className="overflow-x-auto max-h-60">
                        <table className="w-full text-sm">
                          <thead className="bg-red-100 sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-left">Firma / VKN</th>
                              <th className="px-3 py-2 text-left">Tarih</th>
                              <th className="px-3 py-2 text-right text-blue-700">EDM Tutar</th>
                              <th className="px-3 py-2 text-right text-indigo-700">Netsis Tutar</th>
                              <th className="px-3 py-2 text-right text-red-700">FARK</th>
                              <th className="px-3 py-2 text-left text-blue-700">EDM Fatura No</th>
                              <th className="px-3 py-2 text-left text-indigo-700">Netsis Fatura No</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filtreliTutar.map((item, idx) => (
                              <tr key={idx} className="border-t border-red-200 hover:bg-red-100/50">
                                <td className="px-3 py-2">
                                  <div className="text-xs font-medium">{item.edm?.firmaAdi?.slice(0, 25) || item.netsis?.firmaAdi?.slice(0, 25)}</div>
                                  <div className="text-xs text-gray-500 font-mono">{item.edm?.vergiNo || item.netsis?.vergiNo}</div>
                                </td>
                                <td className="px-3 py-2">{item.edm?.tarih || item.netsis?.tarih}</td>
                                <td className="px-3 py-2 text-right font-mono text-blue-700 bg-blue-50">
                                  {item.edm?.toplamTutar?.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺
                                </td>
                                <td className="px-3 py-2 text-right font-mono text-indigo-700 bg-indigo-50">
                                  {item.netsis?.toplamTutar?.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺
                                </td>
                                <td className="px-3 py-2 text-right font-bold text-red-700 bg-red-100">
                                  {item.fark > 0 ? '+' : ''}{item.fark?.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺
                                </td>
                                <td className="px-3 py-2 text-xs text-blue-700">{item.edm?.faturaNo}</td>
                                <td className="px-3 py-2 text-xs text-indigo-700">{item.netsis?.faturaNo}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )})()}

                  {/* Sadece EDM'de Olanlar */}
                  {karsilastirmaSonuc.sadeceEdm.length > 0 && (() => {
                    const aramaLower = karsilastirmaArama.toLowerCase();
                    const filtreliEdm = karsilastirmaSonuc.sadeceEdm.filter(item => {
                      if (!karsilastirmaArama) return true;
                      return (
                        item.faturaNo?.toLowerCase().includes(aramaLower) ||
                        item.firmaAdi?.toLowerCase().includes(aramaLower) ||
                        item.vergiNo?.toLowerCase().includes(aramaLower)
                      );
                    });
                    const edmToplam = filtreliEdm.reduce((t, i) => t + (parseFloat(i.toplamTutar) || 0), 0);
                    return filtreliEdm.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl overflow-hidden">
                      <div className="bg-blue-100 px-4 py-3 border-b border-blue-200 flex justify-between items-center">
                        <div>
                          <h3 className="font-bold text-blue-800 flex items-center gap-2">
                            <Cloud className="w-5 h-5" />
                            Sadece EDM'de Var - Netsis'te YOK ({filtreliEdm.length})
                          </h3>
                          <p className="text-xs text-blue-600 mt-1">Toplam Tutar: {edmToplam.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="h-8 text-xs bg-white" onClick={() => {
                            const columns = [
                              { header: 'Fatura No', accessor: (r) => r.faturaNo },
                              { header: 'Tarih', accessor: (r) => r.tarih },
                              { header: 'Firma Adı', accessor: (r) => r.firmaAdi },
                              { header: 'VKN/TC', accessor: (r) => r.vergiNo },
                              { header: 'Tutar', accessor: (r) => r.toplamTutar }
                            ];
                            exportToExcel(filtreliEdm, columns, 'Sadece_EDM_Faturalar');
                          }}>
                            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 text-xs bg-white" onClick={() => {
                            const columns = [
                              { header: 'Fatura No', accessor: (r) => r.faturaNo },
                              { header: 'Tarih', accessor: (r) => r.tarih },
                              { header: 'Firma', accessor: (r) => r.firmaAdi },
                              { header: 'VKN', accessor: (r) => r.vergiNo },
                              { header: 'Tutar', accessor: (r) => r.toplamTutar }
                            ];
                            exportToPDF(filtreliEdm, columns, `Sadece EDM'de Var (${filtreliEdm.length} adet - ${edmToplam.toLocaleString('tr-TR')} ₺)`, 'Sadece_EDM_Faturalar');
                          }}>
                            <Printer className="w-4 h-4 mr-1" /> PDF
                          </Button>
                        </div>
                      </div>
                      <div className="overflow-x-auto max-h-60">
                        <table className="w-full text-sm">
                          <thead className="bg-blue-100/50 sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-left">Fatura No</th>
                              <th className="px-3 py-2 text-left">Tarih</th>
                              <th className="px-3 py-2 text-left">Firma</th>
                              <th className="px-3 py-2 text-left">Vergi No / TC</th>
                              <th className="px-3 py-2 text-right">Tutar</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filtreliEdm.map((item, idx) => (
                              <tr key={idx} className="border-t border-blue-200 hover:bg-blue-100/30">
                                <td className="px-3 py-2 font-medium text-xs">{item.faturaNo}</td>
                                <td className="px-3 py-2">{item.tarih}</td>
                                <td className="px-3 py-2 text-xs">{item.firmaAdi?.slice(0, 30)}</td>
                                <td className="px-3 py-2 font-mono text-xs">{item.vergiNo}</td>
                                <td className="px-3 py-2 text-right font-mono">
                                  {item.toplamTutar?.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )})()}

                  {/* Sadece Netsis'te Olanlar */}
                  {karsilastirmaSonuc.sadeceNetsis.length > 0 && (() => {
                    const aramaLower = karsilastirmaArama.toLowerCase();
                    const filtreliNetsis = karsilastirmaSonuc.sadeceNetsis.filter(item => {
                      if (!karsilastirmaArama) return true;
                      return (
                        item.faturaNo?.toLowerCase().includes(aramaLower) ||
                        item.firmaAdi?.toLowerCase().includes(aramaLower) ||
                        item.vergiNo?.toLowerCase().includes(aramaLower)
                      );
                    });
                    const netsisToplam = filtreliNetsis.reduce((t, i) => t + (parseFloat(i.toplamTutar) || 0), 0);
                    return filtreliNetsis.length > 0 && (
                    <div className="bg-indigo-50 border border-indigo-200 rounded-xl overflow-hidden">
                      <div className="bg-indigo-100 px-4 py-3 border-b border-indigo-200 flex justify-between items-center">
                        <div>
                          <h3 className="font-bold text-indigo-800 flex items-center gap-2">
                            <Database className="w-5 h-5" />
                            Sadece Netsis'te Var - EDM'de YOK ({filtreliNetsis.length})
                          </h3>
                          <p className="text-xs text-indigo-600 mt-1">Toplam Tutar: {netsisToplam.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="h-8 text-xs bg-white" onClick={() => {
                            const columns = [
                              { header: 'Fatura No', accessor: (r) => r.faturaNo },
                              { header: 'Tarih', accessor: (r) => r.tarih },
                              { header: 'Firma Adı', accessor: (r) => r.firmaAdi },
                              { header: 'VKN/TC', accessor: (r) => r.vergiNo },
                              { header: 'Tutar', accessor: (r) => r.toplamTutar }
                            ];
                            exportToExcel(filtreliNetsis, columns, 'Sadece_Netsis_Faturalar');
                          }}>
                            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 text-xs bg-white" onClick={() => {
                            const columns = [
                              { header: 'Fatura No', accessor: (r) => r.faturaNo },
                              { header: 'Tarih', accessor: (r) => r.tarih },
                              { header: 'Firma', accessor: (r) => r.firmaAdi },
                              { header: 'VKN', accessor: (r) => r.vergiNo },
                              { header: 'Tutar', accessor: (r) => r.toplamTutar }
                            ];
                            exportToPDF(filtreliNetsis, columns, `Sadece Netsis'te Var (${filtreliNetsis.length} adet - ${netsisToplam.toLocaleString('tr-TR')} ₺)`, 'Sadece_Netsis_Faturalar');
                          }}>
                            <Printer className="w-4 h-4 mr-1" /> PDF
                          </Button>
                        </div>
                      </div>
                      <div className="overflow-x-auto max-h-60">
                        <table className="w-full text-sm">
                          <thead className="bg-indigo-100/50 sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-left">Fatura No</th>
                              <th className="px-3 py-2 text-left">Tarih</th>
                              <th className="px-3 py-2 text-left">Firma</th>
                              <th className="px-3 py-2 text-left">Vergi No / TC</th>
                              <th className="px-3 py-2 text-right">Tutar</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filtreliNetsis.map((item, idx) => (
                              <tr key={idx} className="border-t border-indigo-200 hover:bg-indigo-100/30">
                                <td className="px-3 py-2 font-medium text-xs">{item.faturaNo}</td>
                                <td className="px-3 py-2">{item.tarih}</td>
                                <td className="px-3 py-2 text-xs">{item.firmaAdi?.slice(0, 30)}</td>
                                <td className="px-3 py-2 font-mono text-xs">{item.vergiNo}</td>
                                <td className="px-3 py-2 text-right font-mono">
                                  {item.toplamTutar?.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )})()}

                  {/* Hiç Sorun Yoksa */}
                  {(!karsilastirmaSonuc.farklilar || karsilastirmaSonuc.farklilar.length === 0) && 
                   (!karsilastirmaSonuc.tarihFarkli || karsilastirmaSonuc.tarihFarkli.length === 0) &&
                   (!karsilastirmaSonuc.tutarFarkli || karsilastirmaSonuc.tutarFarkli.length === 0) &&
                   karsilastirmaSonuc.sadeceEdm.length === 0 && 
                   karsilastirmaSonuc.sadeceNetsis.length === 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
                      <div className="text-green-500 text-6xl mb-4">✓</div>
                      <h3 className="text-xl font-bold text-green-800">Tüm Faturalar Eşleşiyor!</h3>
                      <p className="text-green-600 mt-2">EDM ve Netsis arasında tutarsızlık bulunamadı.</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Database className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>Karşılaştırma yapmak için tarih aralığı seçin ve "Karşılaştır" butonuna tıklayın.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
